import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useTRPC } from '../../lib/trpc';
import { idem } from '../../lib/idempotency';
import { SectionCard } from '../ui/Detail';
import { Notice } from '../ui/Badge';
import { Button } from '../ui/Button';
import { Skeleton } from '../ui/Skeleton';
import { ErrorState } from '../ui/QueryStates';
import { IconChecklist } from '../icons';

/**
 * Notes on a job or a customer.
 *
 * This card exists because notes were WRITE-ONLY: `note.append` sat on the tech's visit
 * screen and `note.list` had no caller anywhere, so every "customer says it started after
 * the storm" a tech ever typed went into the database and out of reach. A field that
 * silently swallows what people write is worse than not having the field.
 *
 * Notes are append-only by construction — there is no edit and no delete, because the
 * value of "what did we know, and when" collapses the moment it can be rewritten.
 */
export function NotesCard({
  entityType,
  entityId,
  title = 'Notes',
}: {
  entityType: 'customer' | 'property' | 'job' | 'visit';
  entityId: string;
  title?: string;
}) {
  const trpc = useTRPC();
  const qc = useQueryClient();
  const [body, setBody] = useState('');

  const q = useQuery(trpc.note.list.queryOptions({ entityType, entityId }));
  const append = useMutation(
    trpc.note.append.mutationOptions({
      onSuccess: () => {
        setBody('');
        void qc.invalidateQueries();
      },
    }),
  );

  return (
    <SectionCard title={q.data ? `${title} (${q.data.length})` : title}>
      {append.error ? <Notice tone="danger">{append.error.message}</Notice> : null}

      <div className="space-y-2">
        <textarea
          className="min-h-[70px] w-full rounded-[var(--radius)] border border-border bg-surface p-2 text-sm"
          value={body}
          onChange={(e) => setBody(e.target.value)}
          aria-label={`Add a note to this ${entityType}`}
          placeholder="What happened, what you found, what you told them…"
        />
        <div className="flex items-center gap-2">
          <Button
            disabled={!body.trim() || append.isPending}
            onClick={() =>
              append.mutate({ entityType, entityId, body: body.trim(), ...idem() })
            }
          >
            {append.isPending ? 'Saving…' : 'Add note'}
          </Button>
          <span className="text-xs text-muted-foreground">
            Notes can't be edited or deleted once saved.
          </span>
        </div>
      </div>

      <div className="mt-3 border-t border-border pt-3">
        {q.isPending ? (
          <Skeleton className="h-16" />
        ) : q.isError ? (
          <ErrorState
            icon={IconChecklist}
            what="notes"
            onRetry={() => void q.refetch()}
          />
        ) : q.data.length === 0 ? (
          <p className="text-sm text-muted-foreground">
            Nothing written down yet. What gets noted here is what anyone can find in six
            months.
          </p>
        ) : (
          <ul className="space-y-3">
            {q.data.map((n) => (
              <li key={n.id} className="text-sm">
                <p className="whitespace-pre-wrap break-words">{n.body}</p>
                <p className="mt-0.5 text-xs text-muted-foreground">
                  {/* A note whose author left the shop keeps its text and loses its name —
                      "someone" is honest; inventing an attribution would not be. */}
                  {n.authorName ?? n.authorEmail ?? 'Someone'} ·{' '}
                  {new Date(n.createdAt).toLocaleString()}
                  {n.pinned ? (
                    <span className="badge badge--brand ml-2">pinned</span>
                  ) : null}
                </p>
              </li>
            ))}
          </ul>
        )}
      </div>
    </SectionCard>
  );
}
